import { ZodError } from 'zod';
import { env } from '@/env';
import { isAuthFailure } from '@/shared/auth-failure';
import { getAuthToken, redirectToLogin } from '@/shared/auth-token';
import { reportStorageModuleRefusal, STORAGE_MODULE_OFF_MESSAGE } from '@/shared/storage-module';
import {
  StorageMyServicesDataSchema,
  StorageServiceOptionListSchema,
  StorageServiceOptionSuggestionDataSchema,
  type StorageServiceOptionContract,
  type StorageServiceOptionKind
} from '@/contracts/storage-services';

/**
 * API bindings for the "Servicios y condiciones" screen (roadmap §2 "Catálogo de servicios y
 * condiciones", §4 Milestone 4): `GET /storage/service-options` and
 * `GET`/`PATCH /storage/me/services`.
 *
 * Response shapes are parsed through `src/contracts/storage-services.ts`'s Zod schemas (Phase 3.3
 * of the harness-replication roadmap) instead of a hand-written interface with nothing checking it
 * at runtime — see that file's own docblock for exactly which `nexvia-api-web` source each schema
 * was derived from, this session, including a bigger finding beyond F-016 about which endpoint/field
 * name this module actually uses versus what the backend actually ships.
 *
 * DELIBERATELY SELF-CONTAINED — does not import a shared `@/shared/api` or `@/shared/apiClient`.
 * This repo's milestones run as concurrent agents against the same working tree with no file-level
 * isolation between them; while this milestone was in progress, more than one other concurrent
 * milestone independently created its own generic HTTP-transport module under exactly the name
 * this file would otherwise have used (`src/shared/api.ts` was written, by this agent, then
 * overwritten on disk mid-task by a different concurrent milestone's own same-named module; a
 * further `src/shared/apiClient.ts` and `src/shared/storage-api.ts` appeared from others besides).
 * Depending on any of those here would make this screen's correctness hostage to modules this
 * milestone does not own and that were still being written/renamed as this file was finalized.
 * Once the three repos' agents (or a human) reconcile on ONE shared transport module, this file's
 * `request`/`ApiError`/token-reading logic is a mechanical delete-and-reimport away from using it
 * — see this milestone's final report for the concurrency finding in full.
 *
 * **Update, 2026-10-14 (later the same day)**: that reconciliation has now happened for the
 * token-reading piece specifically — `@/shared/auth-token.ts` is the one shared helper every
 * transport module (this one included) now asks for a live token and a 401/403 retry-then-redirect,
 * replacing the `localStorage.getItem(TOKEN_STORAGE_KEY)` read this file used to do on its own (see
 * `getAuthToken`/`redirectToLogin` below). The REQUEST/ENVELOPE logic (`request()`,
 * `unwrapEnvelope`, `parseContract`, `ApiError`) stays exactly as self-contained as the paragraph
 * above describes — only the token-and-retry slice was ever in scope for sharing today.
 *
 * Auth + error-envelope shape mirror this repo's own AGENTS.md and nexvia-web-app's axios
 * interceptor (src/utils/axios.ts): nexvia-api-web's error middleware always answers a failure as
 * `{ success: false, message, code? }` (confirmed against src/shared/middlewares/error.middleware.ts
 * there). `message` travels in English; no page shows it to the user directly (see
 * LOAD_ERROR_MESSAGE/SAVE_ERROR_MESSAGE in ServiciosPage.tsx).
 *
 * **Fixed, 2026-10-14**: the bearer token used to be read straight out of
 * `localStorage[TOKEN_STORAGE_KEY]`, a copy only refreshed by `useAuth.tsx`'s `onIdTokenChanged`
 * listener — which depends on Firebase's own background timer having fired recently, and doesn't
 * fire while a tab is backgrounded or the machine is asleep. A long-backgrounded tab sent the same
 * expired token on every request, and nothing reacted to the resulting failure (a dead token just
 * surfaced as a generic `ApiError` — no retry, no forced refresh, no logout). Now asks
 * `@/shared/auth-token`'s `getAuthToken()` for the live SDK token instead (see that module's own
 * docblock for why that alone fixes it), and retries once with a forced refresh
 * (`getAuthToken(true)`) on a 401 *or* 403 — nexvia-api-web answers an expired-token failure on
 * `/storage/*` routes with 403, not 401 — before giving up and calling `redirectToLogin()`.
 *
 * **Extended, 2026-10-14 (NEX-1151, "Condiciones que imponés por defecto" card)**: `getMyServices`/
 * `updateMyServices` now also carry `minContractMonths`/`noticeDays`/`quoteValidityDays` (see
 * `StorageServicesSelection`'s own field docs below), and a new `suggestServiceOption` binds
 * `POST /storage/service-options/suggest` — the "Agregar otra opción" field. All three endpoint
 * shapes were re-read from `nexvia-api-web`'s current source this session (handlers, zod schemas,
 * the suggestion repository and its HTTP integration test), not assumed from the roadmap doc.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

function parseJsonSafely(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * Some nexvia-api-web storage endpoints answer `{ success: true, data }` (storage.controller.ts's
 * profile/locations/services/requests/quotes handlers); others answer the bare payload directly
 * (storage-document's controller, register-storage's `{...result, phoneVerified, role}`), and
 * `/locations/*` (locations-catalog.ts, reusing this same request()) answers a bare array/object
 * too — confirmed by reading each real controller, not assumed. Detected by shape rather than by
 * path: a body that actually carries BOTH `success` and `data` keys is unwrapped to `.data`;
 * anything else (a bare array included — `in` never matches on one) passes through unchanged.
 * Found live: GET /storage/service-options answers `{success:true,data:[...]}}`, and this file
 * used to return that whole envelope as if it were the array, which crashed `options.map` the
 * first time ServiciosPage.tsx was opened for real (it had only ever been tested against a bare
 * array mock, which is the wrong shape for this specific endpoint).
 */
function unwrapEnvelope(body: unknown): unknown {
  if (body && typeof body === 'object' && !Array.isArray(body) && 'success' in body && 'data' in body) {
    return (body as { data: unknown }).data;
  }
  return body;
}

interface RawResponse {
  ok: boolean;
  status: number;
  body: unknown;
}

async function sendRequest(path: string, init: RequestInit | undefined, token: string | null): Promise<RawResponse> {
  // `new URL(path, base)` rather than plain concatenation: VITE_API_URL carries a trailing slash
  // (same convention as nexvia-web-app's own .env), and `path` starts with one too — string
  // concatenation produced a literal `//` that reached the network as part of the URL.
  const response = await fetch(new URL(path, env.VITE_API_URL).toString(), {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers
    }
  });

  const body = parseJsonSafely(await response.text());
  return { ok: response.ok, status: response.status, body };
}

function toApiError(path: string, raw: RawResponse): ApiError {
  const record = raw.body && typeof raw.body === 'object' ? (raw.body as Record<string, unknown>) : null;
  const message = typeof record?.message === 'string' ? record.message : `Request to ${path} failed with status ${raw.status}`;
  const code = typeof record?.code === 'string' ? record.code : undefined;
  return new ApiError(message, raw.status, code);
}


/**
 * One retry, ever, per call — never a loop: a 401/403 forces a live token refresh
 * (`getAuthToken(true)`) and replays the exact same request once; anything still wrong after that
 * single replay (no refreshed token to retry with, or the replay is *also* 401/403) calls
 * `redirectToLogin()` and throws the same `ApiError` shape this module has always thrown — only the
 * retry-then-redirect behavior around it is new. Mirrors `@/shared/api.ts`'s own retry logic; see
 * this file's docblock for why 403, not just 401, has to be handled here.
 */
async function request(path: string, init?: RequestInit): Promise<unknown> {
  const token = await getAuthToken();
  let raw = await sendRequest(path, init, token);

  // NEX-1164 — only a session failure (401, 403 AUTH_*) retries/redirects; a 403 domain refusal

  // (STORAGE_NOT_APPROVED, STORAGE_ROLE_REQUIRED, ...) falls through to the page as a plain ApiError.

  if (isAuthFailure(raw.status, raw.body)) {
    const refreshedToken = await getAuthToken(true);
    if (refreshedToken) {
      raw = await sendRequest(path, init, refreshedToken);
    }
    if (!refreshedToken || isAuthFailure(raw.status, raw.body)) {
      redirectToLogin();
      throw toApiError(path, raw);
    }
  }

  if (!raw.ok) {
    const error = toApiError(path, raw);
    reportStorageModuleRefusal(error.status, error.code);
    throw error;
  }

  return unwrapEnvelope(raw.body);
}

const apiGet = (path: string): Promise<unknown> => request(path, { method: 'GET' });
const apiPatch = (path: string, payload: unknown): Promise<unknown> =>
  request(path, { method: 'PATCH', body: JSON.stringify(payload) });
const apiPost = (path: string, payload: unknown): Promise<unknown> =>
  request(path, { method: 'POST', body: JSON.stringify(payload) });

/**
 * A 2xx response body that fails its Zod contract (a malformed/unexpected real response) must
 * never let a raw `ZodError` escape to the calling page — that would crash the page exactly the
 * way the original `(current ?? []) is not iterable` / `options.map is not a function` bugs did,
 * which is the one failure mode this whole contracts migration exists to prevent, not reintroduce
 * in a new shape. Re-thrown as this module's own `ApiError`, code `CONTRACT_VIOLATION`, status
 * `502` — same convention as the sibling Phase-3 migrations (`storage-locations-api.ts`,
 * `storage-api.ts`'s `parseResponse`), so existing `ApiError`-catching page code (and any future
 * `describeApiError`-style mapper) degrades to the existing error banner instead of an uncaught
 * crash.
 */
function parseContract<T>(schema: { parse(data: unknown): T }, body: unknown, endpoint: string): T {
  try {
    return schema.parse(body);
  } catch (error) {
    if (error instanceof ZodError) {
      throw new ApiError(`Unexpected response shape from ${endpoint}`, 502, 'CONTRACT_VIOLATION');
    }
    throw error;
  }
}

/**
 * One row of the backoffice-managed catalog (`GET /storage/service-options`). Derived from
 * `src/contracts/storage-services.ts`'s `StorageServiceOptionSchema` (Phase 3.3 of the harness
 * roadmap) instead of hand-written, so this type and the runtime check below can never drift apart
 * the way the old standalone interface could.
 */
export type ServiceOption = StorageServiceOptionContract;

const SERVICE_OPTIONS_PATH = '/storage/service-options';

async function getCatalogOptions(kinds: readonly StorageServiceOptionKind[]): Promise<ServiceOption[]> {
  const path = `${SERVICE_OPTIONS_PATH}?kind=${kinds.join(',')}`;
  const body = await apiGet(path);
  const options = parseContract(StorageServiceOptionListSchema, body, `GET ${path}`);
  // Defensive: a row of another kind is dropped even if the API sent it, so a storage type can
  // never reach a screen that lists the depósito's services.
  return options.filter((option) => kinds.includes(option.kind));
}

/**
 * The depósito's part of the catalog — its services and its conditions (`kind` 'service' and
 * 'condition'), in the order the backend returns it. Not re-sorted client-side: the backing table
 * (nexvia-api-web's `storage_service_options`) already has its own admin-set `display_order`
 * column — re-sorting here would be a frontend opinion about an ordering the backoffice already owns.
 *
 * NEX-1165 — both kinds asked for explicitly instead of relying on the API's default for a bare
 * `GET`: the client's storage types (`kind` 'storage_type') are never a depósito's service. Each
 * screen splits the two kinds itself (`src/shared/service-option-kinds.ts`).
 */
export async function getServiceOptions(): Promise<ServiceOption[]> {
  return getCatalogOptions(['service', 'condition']);
}

/**
 * NEX-1165 — decision A9: the storage types a client picks from (privado, compartido, otro, ...),
 * `kind` 'storage_type'. Read only to name a request's type with its catalog label.
 */
export async function getStorageTypeOptions(): Promise<ServiceOption[]> {
  return getCatalogOptions(['storage_type']);
}

export interface StorageServicesSelection {
  offeredServiceCodes: string[];
  serviceConditions: string;
  /**
   * The three "Condiciones que imponés por defecto" fields (NEX-1151, Claude Design mockup card):
   * minimum contract length in months, cancellation-notice days, and how many days one of this
   * account's quotes stays valid. `getMyServices()` always populates all three (the real backend
   * always sends them, `number | null` — `null` meaning unset, `0` a genuine value, never the same
   * thing); optional here only so a caller that has no use for them — `DepositosPage.tsx` reads
   * this same `getMyServices()` for `offeredServiceCodes` alone, to badge each location card — can
   * keep constructing/mocking a `StorageServicesSelection` without naming fields it never reads.
   * `updateMyServices` treats an omitted field the same as an explicit `null`: see its own docblock.
   */
  minContractMonths?: number | null;
  noticeDays?: number | null;
  quoteValidityDays?: number | null;
}

/**
 * This account's current selection (catalog codes offered + free-text conditions + the three
 * default-condition fields).
 *
 * **Fixed, 2026-10-14**: used to read bare `GET /storage/me`, which never carried this data (see
 * `src/contracts/storage-services.ts`'s own docblock for the full account — every account's
 * selection silently read as empty, always, and a save never persisted). Now calls the real,
 * dedicated `GET /storage/me/services`, wrapped `{success,data:{serviceCodes,serviceConditions}}`.
 * `StorageServicesSelection`'s own field names (`offeredServiceCodes`) are unchanged — only the
 * wire-level endpoint/field name translation below is new, so `ServiciosPage.tsx` needed no change.
 *
 * **Extended, 2026-10-14 (NEX-1151)**: also reads `minContractMonths`/`noticeDays`/
 * `quoteValidityDays` straight off the same response — no renaming at this layer (unlike
 * `serviceCodes` -> `offeredServiceCodes`), since the wire name is already the natural field name
 * and this module's own history is a direct lesson in what a needless rename costs here.
 */
export async function getMyServices(): Promise<StorageServicesSelection> {
  const body = await apiGet('/storage/me/services');
  const data = parseContract(StorageMyServicesDataSchema, body, 'GET /storage/me/services');
  return {
    offeredServiceCodes: data.serviceCodes,
    serviceConditions: data.serviceConditions ?? '',
    minContractMonths: data.minContractMonths,
    noticeDays: data.noticeDays,
    quoteValidityDays: data.quoteValidityDays
  };
}

/**
 * `PATCH /storage/me/services` — replaces this account's offered codes + conditions wholesale,
 * and updates whichever of the three default-condition fields is not `null`.
 *
 * **Fixed, 2026-10-14**: used to send `{offeredServiceCodes, serviceConditions}` — the real
 * backend's `updateStorageServicesBody` schema only recognizes `serviceCodes`, so
 * `offeredServiceCodes` was silently stripped by its default "unknown keys" behavior and a save
 * never actually persisted the chosen codes (`serviceConditions` round-tripped by coincidence,
 * since that one name matched). Now sends the real field name. The response is parsed (not just
 * fire-and-forgotten) so a malformed save response surfaces as a `CONTRACT_VIOLATION` `ApiError`
 * instead of a silent false success; still `Promise<void>` since no caller reads the resolved
 * value (see `ServiciosPage.tsx`'s save handler, which only awaits this for its rejection).
 *
 * **Extended, 2026-10-14 (NEX-1151)**: `minContractMonths`/`noticeDays`/`quoteValidityDays` are
 * each included ONLY when neither `null` nor `undefined` (omitted). `updateStorageServicesBody`
 * validates each as `z.number().int().nonnegative().optional()` — optional, not nullable: the
 * field may be ABSENT, but a literal `null` fails that schema's type check
 * (`VALIDATION_FIELD_INVALID`). Both "never passed a value" and "passed `null`" mean the same
 * thing here — "nothing to send for this field" — which is exactly what omitting the key means to
 * the handler too (its repository layer leaves an omitted field untouched rather than clearing
 * it); there is no way, via this endpoint, to explicitly clear a field that was previously set
 * back to unset.
 */
export async function updateMyServices(payload: StorageServicesSelection): Promise<void> {
  const body = await apiPatch('/storage/me/services', {
    serviceCodes: payload.offeredServiceCodes,
    serviceConditions: payload.serviceConditions,
    ...(payload.minContractMonths != null ? { minContractMonths: payload.minContractMonths } : {}),
    ...(payload.noticeDays != null ? { noticeDays: payload.noticeDays } : {}),
    ...(payload.quoteValidityDays != null ? { quoteValidityDays: payload.quoteValidityDays } : {})
  });
  parseContract(StorageMyServicesDataSchema, body, 'PATCH /storage/me/services');
}

/**
 * `POST /storage/service-options/suggest` — a depósito's suggestion for a service/condition
 * option that is not in the admin-managed catalog (mockup: the "Agregar otra opción" field under
 * "LISTA ADMINISTRABLE"). This is captured for Nexvia to review; it never adds to the catalog
 * `getServiceOptions()` reads, and the suggested label is never immediately selectable — there is
 * deliberately no read/list method here to go with it (see
 * `storage-service-option-suggestion.repository.ts`'s own docblock: admin-side review is an
 * explicit, separate follow-up).
 *
 * `Promise<void>`, same as `updateMyServices` above and for the same reason: `ServiciosPage.tsx`
 * only needs to know whether the suggestion was saved, not read anything back off it — but the
 * response is still parsed through `StorageServiceOptionSuggestionDataSchema`, not
 * fire-and-forgotten, so a malformed 2xx surfaces as `CONTRACT_VIOLATION` rather than a silent
 * false success.
 */
export async function suggestServiceOption(label: string): Promise<void> {
  const body = await apiPost('/storage/service-options/suggest', { label });
  parseContract(StorageServiceOptionSuggestionDataSchema, body, 'POST /storage/service-options/suggest');
}

// --- Error copy (audit S6) ------------------------------------------------------------------------

/**
 * Audit S6 — the codes `PATCH /storage/me/services` and `POST /storage/service-options/suggest`
 * answer (nexvia-api-web `domain/storage-services.validators.ts`,
 * `create-storage-service-option-suggestion.handler.ts`), mapped to Spanish by code, never by the
 * English `message`. `VALIDATION_ENUM_INVALID` on a save is `assertKnownServiceCodes`: a checked
 * service Nexvia deactivated (or moved to another kind) since the page loaded.
 */
const SERVICES_ERROR_MESSAGES: Record<string, string> = {
  VALIDATION_ENUM_INVALID:
    'Uno de los servicios o condiciones que marcaste ya no está disponible: Nexvia lo desactivó. Recargá la página para ver la lista actualizada y guardá de nuevo.',
  STORAGE_SERVICE_CODES_INVALID: 'No pudimos leer los servicios que marcaste. Recargá la página y probá de nuevo.',
  STORAGE_SERVICE_CONDITIONS_INVALID: 'Las condiciones no tienen un formato válido. Revisá el texto y probá de nuevo.',
  STORAGE_SERVICE_OPTION_SUGGESTION_LABEL_REQUIRED: 'Escribí la opción que querés sugerir.',
  VALIDATION_FIELD_TOO_LONG: 'El texto es demasiado largo. Acortalo y probá de nuevo.',
  // Audit CTR-3 — the module closed after the page loaded (`require-storage-module.middleware.ts`).
  STORAGE_MODULE_DISABLED: STORAGE_MODULE_OFF_MESSAGE,
  CONTRACT_VIOLATION: 'No pudimos procesar la respuesta del servidor. Intentá de nuevo en unos minutos.'
};

/** Audit S6 — the Spanish message of a services/suggestion error; `fallback` for any other code. */
export function describeServicesError(err: unknown, fallback: string): string {
  return (err instanceof ApiError && err.code && SERVICES_ERROR_MESSAGES[err.code]) || fallback;
}
